import { useEffect, useRef, useState, type FormEvent } from 'react';
import { SMTP_REASON } from '../../lib/mail-reasons';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Shield, ShieldCheck } from 'lucide-react';
import { apiFetch, isApiError } from '../../lib/api';
import { resolveAvatarUrl, serverOrigin } from '../../lib/server-origin';
import integrateGuide from '../../../../../docs/integrate-openline.md?raw';
import { Tabs, type TabItem } from '../../components/Tabs';
import { RoutingSection } from './RoutingSection';
import { Avatar } from '../../components/Avatar';
import { WebhooksSection, WebhookLogsSection } from './WebhooksTab';
import { KnowledgeBaseSection } from './KnowledgeBaseSection';
import { IntentsSection } from './IntentsSection';
import { RagEvalSection } from './RagEvalSection';
import { invalidateCommandsCache } from '../../lib/commands-api';
import { COUNTRIES } from '@prakash-software/shared';
import { ChannelsSection } from './ChannelsSection';
import { ShopifySection } from './ShopifySection';
import { CsatSection } from './CsatSection';
import { FlowsSection } from './FlowsSection';
import { useFeatureFlag } from '../../stores/feature-flags';
import { StringListEditor } from './StringListEditor';
import { PlaygroundSection } from './PlaygroundSection';
import { SuggestionsSection } from './SuggestionsSection';
import { EmailSection } from './EmailSection';
import { NewsletterSection } from './NewsletterSection';
import { TidioImportSection } from './TidioImportSection';
import { COMPANY_TONES, type CompanyProfile, type CompanyTone } from '@prakash-software/shared';
import { useDialogFocus } from '../../lib/dialog-focus';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

// The site's settings, grouped the way an admin thinks about them: set the
// site up, shape the chat widget, train and test the assistant, automate,
// connect channels and other systems, manage the team. The selection lives in
// `?tab=` so a section can be linked to and survives a reload.
const SITE_TABS: TabItem[] = [
  { id: 'general', label: 'General', group: 'Site' },
  { id: 'integration', label: 'Install on your site', group: 'Site' },
  { id: 'import', label: 'Import from Tidio', group: 'Site' },
  { id: 'appearance', label: 'Appearance', group: 'Chat widget' },
  { id: 'launcher', label: 'Launcher & visibility', group: 'Chat widget' },
  { id: 'messaging', label: 'Messages & starters', group: 'Chat widget' },
  { id: 'prechat', label: 'Visitor form & newsletter', group: 'Chat widget' },
  { id: 'access', label: 'Access & security', group: 'Chat widget' },
  { id: 'ai', label: 'Company & instructions', group: 'AI assistant' },
  { id: 'knowledge', label: 'Data sources', group: 'AI assistant' },
  { id: 'suggestions', label: 'Suggestions', group: 'AI assistant' },
  { id: 'playground', label: 'Playground', group: 'AI assistant' },
  { id: 'ai-eval', label: 'AI evaluation', group: 'AI assistant' },
  { id: 'intents', label: 'Intents', group: 'AI assistant' },
  { id: 'handoff', label: 'Human handoff', group: 'Automation' },
  { id: 'followup', label: 'Follow-ups', group: 'Automation' },
  { id: 'flows', label: 'Flows', group: 'Automation' },
  { id: 'routing', label: 'Routing', group: 'Automation' },
  { id: 'commands', label: 'Commands', group: 'Automation' },
  { id: 'csat', label: 'CSAT', group: 'Automation' },
  { id: 'channels', label: 'Channels', group: 'Channels & integrations' },
  { id: 'shopify', label: 'Shopify', group: 'Channels & integrations' },
  { id: 'email', label: 'Email (SMTP)', group: 'Channels & integrations' },
  { id: 'newsletter', label: 'Newsletter', group: 'Channels & integrations' },
  { id: 'webhooks', label: 'Webhooks', group: 'Channels & integrations' },
  { id: 'webhook-logs', label: 'Webhook logs', group: 'Channels & integrations' },
  { id: 'agents', label: 'Agents', group: 'Team' },
];

// Sections that edit the widget_configs row: they share one draft and one
// Save bar, so switching between them keeps unsaved edits.
const WIDGET_CONFIG_TABS = new Set(['appearance', 'launcher', 'messaging', 'prechat', 'access', 'ai', 'handoff', 'followup']);

// Earlier links named the whole widget panel; land them on its first section.
const LEGACY_TABS: Record<string, string> = { widget: 'appearance' };

// Curated Google Fonts offered in the widget customization dropdown. The
// widget loads the chosen family via an injected <link> at runtime.
const GOOGLE_FONTS = [
  'Onest', 'Inter', 'Manrope', 'DM Sans', 'Poppins', 'Work Sans', 'Nunito',
  'Source Sans 3', 'Montserrat', 'Lato', 'Roboto', 'Open Sans',
  'IBM Plex Sans', 'Space Grotesk', 'Fraunces', 'Playfair Display',
] as const;

interface SiteCommand {
  id: string;
  siteId?: string;
  shortcut: string;
  label: string;
  body: string;
  sortOrder: number;
  /** null = team command. Personal ones belong in the agent's Commands page. */
  ownerAgentId?: string | null;
}

interface Site {
  id: string; slug: string; name: string; origin: string;
  embedToken: string; embedTokenV: number;
  createdAt: string; archivedAt: string | null;
}
interface SiteAgent {
  agentId: string; isSiteAdmin: boolean; assignedAt: string;
  displayName: string; email: string; role: string; disabledAt: string | null;
  avatarUrl: string | null; phone: string | null;
}
interface Agent { id: string; email: string; displayName: string; role: string; disabledAt: string | null; }
interface WidgetConfig {
  siteId: string;
  primaryColor: string;
  accentColor: string;
  accent2Color: string | null;
  paperColor: string;
  borderColor: string | null;
  bubblePosition: 'bottom-right' | 'bottom-left';
  launcherOffsetX: number | null;
  launcherOffsetY: number | null;
  launcherVisibility: 'all' | 'desktop' | 'mobile';
  hiddenPaths: string[];
  conversationStarters: string[];
  cornerStyle: 'square' | 'rounded' | 'pill';
  fontFamily: string | null;
  launcherDisplay: 'icon' | 'text' | 'both' | 'video' | 'video-large';
  launcherText: string;
  launcherIconUrl: string | null;
  launcherVideoUrl: string | null;
  launcherVideoSize: 'small' | 'medium' | 'large';
  launcherStatusText: string | null;
  headerText: string | null;
  chatTitle: string | null;
  hideSystemBubbles: boolean;
  footerText: string | null;
  greeting: string | null;
  offlineCopy: string | null;
  // The site's only AI setting — provider, model and key are system-wide
  // (Admin → System → AI). See migration 0040.
  systemPrompt: string | null;
  companyProfile: CompanyProfile | null;
  readMoreLinks: boolean;
  humanHandoffButton: boolean;
  handoffOfflineMessage: string | null;
  handoffNotifyEmails: string[];
  followupEnabled: boolean;
  followupAfterMinutes: number;
  followupMessage: string | null;
  followupMax: number;
  followupAgentChats: boolean;
  newsletterEnabled: boolean;
  newsletterLabel: string | null;
  /** Read-only here (edited under Webhooks); follow-ups must come before it. */
  autoEndMinutes: number;
  intentHandoffEnabled: boolean;
  formMode: 'required' | 'optional' | 'hidden';
  prechatStyle: 'form' | 'chat';
  chatRouting: 'ai-first' | 'agent-first';
  prechatFields: {
    name: { collect: boolean; required: boolean };
    email: { collect: boolean; required: boolean };
    phone: { collect: boolean; required: boolean };
  };
  captureAfter: number;
  prechatPrompts: {
    intro?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    retryEmail?: string | null;
    retryPhone?: string | null;
  } | null;
  allowedOrigins: string[];
  allowedIps: string[];
  blockedIps: string[];
  blockedCountries: string[];
  requireAgentOnline: boolean;
}

// Built-in conversational-capture wording, mirrored from the widget
// (packages/widget/src/views/capture.ts) so the admin inputs can show the
// active default as a placeholder. Keep in sync with that file.
const PRECHAT_DEFAULTS: Record<'name' | 'email' | 'phone', { question: string; retry: string }> = {
  name: { question: 'Before we go on — what should we call you?', retry: '' },
  email: {
    question: 'Thanks! What’s the best email to reach you?',
    retry: 'That email doesn’t look quite right — mind trying again?',
  },
  phone: {
    question: 'And a phone number we can reach you on?',
    retry: 'Hmm, that number didn’t validate. Try including your country code, e.g. +91 …',
  },
};

/** Why the "you've been added to a site" mail did not go out, actionably. */
const ASSIGN_REASON: Record<string, string> = {
  ...SMTP_REASON,
  'no-public-url': 'PUBLIC_APP_URL is not set on the server',
  'agent-disabled': 'that account is disabled, so it cannot sign in',
  'already-assigned': 'they were already on this site',
  internal: 'an unexpected error — check the server log',
};

export function AdminSiteDetail() {
  const { siteId = '' } = useParams<{ siteId: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [siteAgents, setSiteAgents] = useState<SiteAgent[]>([]);
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [configDraft, setConfigDraft] = useState<WidgetConfig | null>(null);
  const [picker, setPicker] = useState('');
  // Reports whether the newly-added agent was actually emailed. Not an error
  // (the assignment worked) but not silent either — if the mail failed, the
  // admin is the only one who can tell them.
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // ?tab= is the selected section: linkable (the Instagram OAuth redirect
  // lands on Channels with it) and kept across reloads.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? 'general';
  const tab = LEGACY_TABS[requestedTab] ?? requestedTab;
  const layoutRef = useRef<HTMLDivElement>(null);
  const setTab = (id: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', id);
        return next;
      },
      { replace: true },
    );
    // The section list is long; picking a link near its end would leave the
    // new panel (which sits at the top) scrolled out of view. Bring it back.
    layoutRef.current?.scrollIntoView({ block: 'start' });
  };
  const flowsEnabled = useFeatureFlag('flows');
  // Editable site identity (name + origin) — saved via PATCH /api/admin/sites/:id.
  const [siteDraft, setSiteDraft] = useState<{ name: string; origin: string } | null>(null);

  async function load() {
    try {
      const [{ sites }, { siteAgents: sa }, { agents }, cfgRes] = await Promise.all([
        apiFetch<{ sites: Site[] }>('/api/admin/sites'),
        apiFetch<{ siteAgents: SiteAgent[] }>(`/api/admin/sites/${siteId}/agents`),
        apiFetch<{ agents: Agent[] }>('/api/admin/agents'),
        apiFetch<{ widgetConfig: WidgetConfig }>(`/api/admin/sites/${siteId}/widget-config`)
          .catch(() => null),
      ]);
      const found = sites.find((s) => s.id === siteId) ?? null;
      setSite(found);
      if (found) setSiteDraft({ name: found.name, origin: found.origin });
      setSiteAgents(sa);
      setAllAgents(agents);
      if (cfgRes) {
        setConfig(cfgRes.widgetConfig);
        setConfigDraft(cfgRes.widgetConfig);
      }
    } catch {
      setError('load-failed');
    }
  }
  // load() is recreated every render; it only needs to re-run when the site changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [siteId]);

  async function saveConfig() {
    if (!configDraft) return;
    // Lead-capture guarantee: the conversational pre-chat must ask for a name
    // plus at least one of email/phone (and require them), so visitor.identified
    // can fire. Enforced here since that's where per-field config is edited.
    if (configDraft.prechatStyle === 'chat') {
      const f = configDraft.prechatFields;
      const nameOk = f.name.collect && f.name.required;
      const contactOk =
        (f.email.collect && f.email.required) || (f.phone.collect && f.phone.required);
      if (!nameOk || !contactOk) {
        setError('Pre-chat must require a name and at least one of email or phone.');
        return;
      }
    }
    setError(null);
    setSaving(true);
    try {
      const patch: Partial<WidgetConfig> = {
        primaryColor: configDraft.primaryColor,
        accentColor: configDraft.accentColor,
        accent2Color: configDraft.accent2Color,
        paperColor: configDraft.paperColor,
        borderColor: configDraft.borderColor,
        bubblePosition: configDraft.bubblePosition,
        launcherOffsetX: configDraft.launcherOffsetX,
        launcherOffsetY: configDraft.launcherOffsetY,
        launcherVisibility: configDraft.launcherVisibility,
        hiddenPaths: configDraft.hiddenPaths,
        conversationStarters: configDraft.conversationStarters,
        cornerStyle: configDraft.cornerStyle,
        fontFamily: configDraft.fontFamily,
        launcherDisplay: configDraft.launcherDisplay,
        launcherText: configDraft.launcherText,
        launcherVideoSize: configDraft.launcherVideoSize,
        launcherStatusText: configDraft.launcherStatusText,
        headerText: configDraft.headerText,
        chatTitle: configDraft.chatTitle,
        hideSystemBubbles: configDraft.hideSystemBubbles,
        footerText: configDraft.footerText,
        greeting: configDraft.greeting,
        offlineCopy: configDraft.offlineCopy,
        systemPrompt: configDraft.systemPrompt,
        companyProfile: configDraft.companyProfile,
        readMoreLinks: configDraft.readMoreLinks,
        humanHandoffButton: configDraft.humanHandoffButton,
        handoffOfflineMessage: configDraft.handoffOfflineMessage,
        handoffNotifyEmails: configDraft.handoffNotifyEmails,
        followupEnabled: configDraft.followupEnabled,
        followupAfterMinutes: configDraft.followupAfterMinutes,
        followupMessage: configDraft.followupMessage,
        followupMax: configDraft.followupMax,
        followupAgentChats: configDraft.followupAgentChats,
        newsletterEnabled: configDraft.newsletterEnabled,
        newsletterLabel: configDraft.newsletterLabel,
        formMode: configDraft.formMode,
        prechatStyle: configDraft.prechatStyle,
        chatRouting: configDraft.chatRouting,
        prechatFields: configDraft.prechatFields,
        captureAfter: configDraft.captureAfter,
        prechatPrompts: configDraft.prechatPrompts,
        allowedOrigins: configDraft.allowedOrigins,
        allowedIps: configDraft.allowedIps,
        blockedIps: configDraft.blockedIps,
        blockedCountries: configDraft.blockedCountries,
        requireAgentOnline: configDraft.requireAgentOnline,
      };
      const { widgetConfig } = await apiFetch<{ widgetConfig: WidgetConfig }>(
        `/api/admin/sites/${siteId}/widget-config`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
      setConfig(widgetConfig);
      setConfigDraft(widgetConfig);
    } catch (err) {
      const e = err as { body?: { issues?: unknown } };
      setError(e.body?.issues ? JSON.stringify(e.body.issues) : 'save-failed');
    } finally {
      setSaving(false);
    }
  }

  function setDraft<K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) {
    setConfigDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function setPrechatField(
    field: 'name' | 'email' | 'phone',
    patch: Partial<{ collect: boolean; required: boolean }>,
  ) {
    setConfigDraft((d) =>
      d
        ? {
            ...d,
            prechatFields: {
              ...d.prechatFields,
              [field]: { ...d.prechatFields[field], ...patch },
            },
          }
        : d,
    );
  }

  // Capture copy override. An empty input clears the key back to null so the
  // widget falls back to its built-in default.
  function setPrechatPrompt(
    key: 'intro' | 'name' | 'email' | 'phone' | 'retryEmail' | 'retryPhone',
    value: string,
  ) {
    setConfigDraft((d) =>
      d ? { ...d, prechatPrompts: { ...d.prechatPrompts, [key]: value || null } } : d,
    );
  }

  async function saveSite() {
    if (!siteDraft || !site) return;
    setSaving(true);
    setError(null);
    try {
      const { site: updated } = await apiFetch<{ site: Site }>(`/api/admin/sites/${siteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: siteDraft.name.trim(), origin: siteDraft.origin.trim() }),
      });
      setSite(updated);
      setSiteDraft({ name: updated.name, origin: updated.origin });
    } catch (err) {
      const e = err as { body?: { issues?: unknown } };
      setError(e.body?.issues ? JSON.stringify(e.body.issues) : 'save-failed');
    } finally {
      setSaving(false);
    }
  }

  const iconInputRef = useRef<HTMLInputElement>(null);
  async function uploadIcon(file: File) {
    if (file.size > 512 * 1024) {
      setError('icon-too-large (max 512 KB)');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Raw image bytes; Content-Type drives the server-side extension.
      const { widgetConfig } = await apiFetch<{ widgetConfig: WidgetConfig }>(
        `/api/admin/sites/${siteId}/widget-icon`,
        { method: 'POST', body: file, headers: { 'Content-Type': file.type } },
      );
      setConfig(widgetConfig);
      setConfigDraft(widgetConfig);
    } catch {
      setError('icon-upload-failed');
    } finally {
      setSaving(false);
    }
  }

  const videoInputRef = useRef<HTMLInputElement>(null);
  async function uploadVideo(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      setError('video-too-large (max 10 MB)');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Raw video bytes; Content-Type drives the server-side extension.
      const { widgetConfig } = await apiFetch<{ widgetConfig: WidgetConfig }>(
        `/api/admin/sites/${siteId}/widget-video`,
        { method: 'POST', body: file, headers: { 'Content-Type': file.type } },
      );
      setConfig(widgetConfig);
      setConfigDraft(widgetConfig);
    } catch {
      setError('video-upload-failed');
    } finally {
      setSaving(false);
    }
  }

  async function removeVideo() {
    if (!(await confirmDialog({ title: 'Remove the launcher video?', message: 'If the launcher is set to Video, it will fall back to icon + text.', confirmLabel: 'Remove video' }))) return;
    setSaving(true);
    setError(null);
    try {
      const { widgetConfig } = await apiFetch<{ widgetConfig: WidgetConfig }>(
        `/api/admin/sites/${siteId}/widget-video`,
        { method: 'DELETE' },
      );
      setConfig(widgetConfig);
      setConfigDraft(widgetConfig);
    } catch {
      setError('video-remove-failed');
    } finally {
      setSaving(false);
    }
  }

  async function rotateToken() {
    if (!(await confirmDialog({ title: 'Rotate the embed token?', message: 'Old token works for 24 hours per the spec, but enforcement is Section 14.0 — for now the old token stops working immediately.', confirmLabel: 'Rotate token' }))) return;
    await apiFetch(`/api/admin/sites/${siteId}/rotate-token`, { method: 'POST' });
    await load();
  }

  async function addAgent() {
    if (!picker) return;
    const name = allAgents.find((a) => a.id === picker)?.displayName ?? 'the agent';
    setNotice(null);
    const res = await apiFetch<{ notified?: { sent: boolean; reason?: string } }>(
      `/api/admin/sites/${siteId}/agents`,
      { method: 'POST', body: JSON.stringify({ agentId: picker, isSiteAdmin: false }) },
    );
    // The assignment succeeded either way — this only reports whether the
    // agent was TOLD. Left unsaid, someone gains a site they never look at.
    const n = res.notified;
    setNotice(
      n?.sent
        ? `${name} added and notified by email.`
        : `${name} added, but not emailed: ${ASSIGN_REASON[n?.reason ?? ''] ?? n?.reason ?? 'unknown reason'}.`,
    );
    setPicker('');
    await load();
  }

  async function removeAgent(agentId: string) {
    if (!(await confirmDialog({ title: 'Remove this agent from the site?', message: 'You can add them back at any time.', confirmLabel: 'Remove' }))) return;
    await apiFetch(`/api/admin/sites/${siteId}/agents/${agentId}`, { method: 'DELETE' });
    await load();
  }

  async function toggleSiteAdmin(agentId: string, current: boolean) {
    await apiFetch(`/api/admin/sites/${siteId}/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isSiteAdmin: !current }),
    });
    await load();
  }

  if (error) return <p className={styles.error} role="alert">{error}</p>;
  if (!site) return <p>Loading…</p>;

  const assignedIds = new Set(siteAgents.map((a) => a.agentId));
  const availableAgents = allAgents.filter((a) => !assignedIds.has(a.id) && !a.disabledAt);

  // The widget reads its site token from `data-site-token` and derives the
  // server URL from this script's own origin, so one hosted bundle serves
  // every site. serverOrigin() resolves the deploy origin (handles 'auto').
  const embedSnippet = `<script
  src="${serverOrigin()}/openline.js"
  data-site-token="${site.embedToken}"
  async defer
></script>`;

  return (
    <>
      <Link to="/admin/sites" className={styles.backLink}>← All sites</Link>

      <section className={styles.section}>
        <p className={styles.kicker}>Site · {site.slug}</p>
        <h2 className={styles.title}>{site.name}</h2>
      </section>

      <div className={styles.settingsLayout} ref={layoutRef}>
        <Tabs
          items={flowsEnabled ? SITE_TABS : SITE_TABS.filter((t) => t.id !== 'flows')}
          active={tab}
          onChange={setTab}
          orientation="vertical"
          ariaLabel="Site settings"
        />
        <div className={styles.settingsPanel}>

      {tab === 'general' && (
        <>
          {siteDraft && (
            <section className={styles.section}>
              <p className={styles.kicker}>General</p>
              <div className={styles.form}>
                <label className={styles.field}>
                  <span className={styles.label}>Site name</span>
                  <input
                    className={styles.input}
                    type="text"
                    maxLength={120}
                    value={siteDraft.name}
                    onChange={(e) => setSiteDraft({ ...siteDraft, name: e.target.value })}
                    placeholder="Acme Inc."
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Primary origin (site URL)</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={siteDraft.origin}
                    onChange={(e) => setSiteDraft({ ...siteDraft, origin: e.target.value })}
                    placeholder="https://example.com"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Slug (read-only)</span>
                  <input className={styles.input} type="text" value={site.slug} readOnly disabled />
                </label>
                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => void saveSite()}
                    disabled={
                      saving ||
                      siteDraft.name.trim().length === 0 ||
                      (siteDraft.name.trim() === site.name &&
                        siteDraft.origin.trim() === site.origin)
                    }
                  >
                    {saving ? 'Saving…' : 'Save changes →'}
                  </button>
                  <button
                    type="button"
                    className={styles.buttonGhost}
                    onClick={() => setSiteDraft({ name: site.name, origin: site.origin })}
                    disabled={saving}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </section>
          )}

          <section className={styles.section}>
            <p className={styles.kicker}>Embed snippet</p>
        <p style={{ marginTop: 0, color: 'var(--inic-ink-subtle)', fontSize: 'var(--inic-size-sm)' }}>
          Paste on any page whose origin is listed under Allowed embed origins below.
        </p>
        <div className={styles.embedBlock}>{embedSnippet}</div>
        <div className={styles.actions}>
          <button type="button" className={styles.buttonGhost}
            onClick={() => navigator.clipboard?.writeText(embedSnippet)}>
            Copy embed code
          </button>
          <button type="button" className={styles.buttonGhost}
            onClick={() => navigator.clipboard?.writeText(site.embedToken)}>
            Copy token
          </button>
          <button type="button" className={styles.buttonGhost} onClick={rotateToken}>
            Rotate token
          </button>
        </div>
      </section>
        </>
      )}

      {tab === 'integration' && <IntegrationSection site={site} />}

      {WIDGET_CONFIG_TABS.has(tab) && configDraft && (
        <section className={styles.section}>
          <p className={styles.kicker}>{SITE_TABS.find((t) => t.id === tab)?.label}</p>

          {tab === 'appearance' && (
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Text (ink) color</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={configDraft.primaryColor}
                    onChange={(e) => setDraft('primaryColor', e.target.value)}
                    style={{ width: 38, height: 34, padding: 0, border: 'none', background: 'none' }}
                    aria-label="Text color swatch"
                  />
                  <input
                    className={styles.input}
                    type="text"
                    value={configDraft.primaryColor}
                    onChange={(e) => setDraft('primaryColor', e.target.value)}
                    placeholder="#1A1614"
                  />
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Accent color</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={configDraft.accentColor}
                    onChange={(e) => setDraft('accentColor', e.target.value)}
                    style={{ width: 38, height: 34, padding: 0, border: 'none', background: 'none' }}
                    aria-label="Accent color swatch"
                  />
                  <input
                    className={styles.input}
                    type="text"
                    value={configDraft.accentColor}
                    onChange={(e) => setDraft('accentColor', e.target.value)}
                    placeholder="#B5331D"
                  />
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Background color</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={configDraft.paperColor}
                    onChange={(e) => setDraft('paperColor', e.target.value)}
                    style={{ width: 38, height: 34, padding: 0, border: 'none', background: 'none' }}
                    aria-label="Background color swatch"
                  />
                  <input
                    className={styles.input}
                    type="text"
                    value={configDraft.paperColor}
                    onChange={(e) => setDraft('paperColor', e.target.value)}
                    placeholder="#F6F3EC"
                  />
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>System accent</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={configDraft.accent2Color ?? configDraft.accentColor}
                    onChange={(e) => setDraft('accent2Color', e.target.value)}
                    style={{ width: 38, height: 34, padding: 0, border: 'none', background: 'none' }}
                    aria-label="Secondary accent swatch"
                  />
                  <input
                    className={styles.input}
                    type="text"
                    value={configDraft.accent2Color ?? ''}
                    onChange={(e) => setDraft('accent2Color', e.target.value || null)}
                    placeholder="(blank = same as accent)"
                  />
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Message border color</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={configDraft.borderColor ?? '#e7e0d2'}
                    onChange={(e) => setDraft('borderColor', e.target.value)}
                    style={{ width: 38, height: 34, padding: 0, border: 'none', background: 'none' }}
                    aria-label="Message border color swatch"
                  />
                  <input
                    className={styles.input}
                    type="text"
                    value={configDraft.borderColor ?? ''}
                    onChange={(e) => setDraft('borderColor', e.target.value || null)}
                    placeholder="(blank = default border)"
                  />
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Roundness</span>
                <select
                  className={styles.select}
                  value={configDraft.cornerStyle}
                  onChange={(e) => setDraft('cornerStyle', e.target.value as WidgetConfig['cornerStyle'])}
                >
                  <option value="square">Square</option>
                  <option value="rounded">Rounded</option>
                  <option value="pill">Pill</option>
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Font</span>
                <select
                  className={styles.select}
                  value={configDraft.fontFamily ?? ''}
                  onChange={(e) => setDraft('fontFamily', e.target.value || null)}
                >
                  <option value="">Default (bundled)</option>
                  {GOOGLE_FONTS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {tab === 'launcher' && (
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Bubble position</span>
                <select
                  className={styles.select}
                  value={configDraft.bubblePosition}
                  onChange={(e) =>
                    setDraft('bubblePosition', e.target.value as WidgetConfig['bubblePosition'])
                  }
                >
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                </select>
              </label>
              <OffsetField
                label="Side offset (px)"
                value={configDraft.launcherOffsetX}
                onChange={(v) => setDraft('launcherOffsetX', v)}
              />
              <OffsetField
                label="Bottom offset (px)"
                value={configDraft.launcherOffsetY}
                onChange={(v) => setDraft('launcherOffsetY', v)}
              />
              <label className={styles.field}>
                <span className={styles.label}>Show on</span>
                <select
                  className={styles.select}
                  value={configDraft.launcherVisibility}
                  onChange={(e) =>
                    setDraft('launcherVisibility', e.target.value as WidgetConfig['launcherVisibility'])
                  }
                >
                  <option value="all">Desktop and mobile</option>
                  <option value="desktop">Desktop only</option>
                  <option value="mobile">Mobile only</option>
                </select>
                <span className={styles.hint}>Mobile means a screen 640 px wide or narrower.</span>
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Hide on these pages</span>
                <StringListEditor
                  value={configDraft.hiddenPaths}
                  onChange={(next) => setDraft('hiddenPaths', next)}
                  placeholder="/checkout*"
                  addLabel="Hide here"
                  emptyText="Shown on every page."
                  max={50}
                  maxLength={200}
                  mono
                />
                <span className={styles.hint}>
                  A page path such as <code>/pricing</code>, or use <code>*</code> to match many:{' '}
                  <code>/checkout*</code> hides every checkout page. A conversation already open stays
                  open.
                </span>
              </div>
              <label className={styles.field}>
                <span className={styles.label}>Launcher shows</span>
                <select
                  className={styles.select}
                  value={configDraft.launcherDisplay}
                  onChange={(e) =>
                    setDraft('launcherDisplay', e.target.value as WidgetConfig['launcherDisplay'])
                  }
                >
                  <option value="both">Icon + text</option>
                  <option value="icon">Icon only</option>
                  <option value="text">Text only</option>
                  <option value="video">Video preview (minimized)</option>
                  <option value="video-large">Video preview (maximized)</option>
                </select>
                <span className={styles.hint}>
                  Maximized opens the looping video as an expanded card (sized by “Maximized
                  video width” below); minimized keeps it bubble-sized.
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Maximized video width</span>
                <select
                  className={styles.select}
                  value={configDraft.launcherVideoSize}
                  onChange={(e) =>
                    setDraft('launcherVideoSize', e.target.value as WidgetConfig['launcherVideoSize'])
                  }
                  disabled={
                    configDraft.launcherDisplay !== 'video' &&
                    configDraft.launcherDisplay !== 'video-large'
                  }
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
                <span className={styles.hint}>
                  Width of the expanded video card. The video fills the width; height follows its
                  aspect ratio. Applies to the Video preview modes.
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Launcher status text</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={60}
                  value={configDraft.launcherStatusText ?? ''}
                  onChange={(e) => setDraft('launcherStatusText', e.target.value || null)}
                  placeholder="Live support available"
                />
                <span className={styles.hint}>
                  Small tagline shown on the launcher. Blank uses the default “Live support
                  available”.
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Button text</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={40}
                  value={configDraft.launcherText}
                  onChange={(e) => setDraft('launcherText', e.target.value)}
                  placeholder="Talk to us"
                />
                <span className={styles.hint}>
                  The call-to-action shown as the launcher’s button, below the status text.
                </span>
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--inic-space-2)' }}>
                  <input
                    type="checkbox"
                    checked={configDraft.requireAgentOnline}
                    onChange={(e) => setDraft('requireAgentOnline', e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--inic-space-1)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--inic-space-2)' }}>
                      <span className={styles.label} style={{ textTransform: 'none' }}>
                        Only show the widget when an agent is online
                      </span>
                      <AgentOnlineHelp />
                    </span>
                    <span className={styles.hint}>
                      With no agent online the launcher is hidden (the embed stays connected and
                      reappears the moment one comes online) and the host page is notified so it can
                      render its own fallback. Off = the widget always renders.
                    </span>
                  </span>
                </label>
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Launcher icon</span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-3)', alignItems: 'center' }}>
                  {configDraft.launcherIconUrl ? (
                    <img
                      src={resolveAvatarUrl(configDraft.launcherIconUrl)}
                      alt="Current launcher icon"
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        borderRadius: 'var(--inic-radius-sm)',
                        border: '1px solid var(--inic-rule)',
                      }}
                    />
                  ) : (
                    <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>
                      No icon — the widget shows the site initials.
                    </span>
                  )}
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadIcon(f);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className={styles.buttonGhost}
                    onClick={() => iconInputRef.current?.click()}
                    disabled={saving}
                  >
                    {configDraft.launcherIconUrl ? 'Replace icon' : 'Upload icon'}
                  </button>
                </span>
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Launcher video — looping muted preview (shown when “Launcher shows” is Video; mp4/webm, ≤ 10 MB)
                </span>
                <span style={{ display: 'flex', gap: 'var(--inic-space-3)', alignItems: 'center' }}>
                  {configDraft.launcherVideoUrl ? (
                    <video
                      src={resolveAvatarUrl(configDraft.launcherVideoUrl)}
                      muted
                      loop
                      autoPlay
                      playsInline
                      ref={(v) => {
                        // React doesn't reliably set the `muted` attribute; force
                        // the property so the preview never plays audio.
                        if (v) v.muted = true;
                      }}
                      style={{
                        // Match the widget: bound the box and keep the video's own
                        // aspect ratio (no crop, no letterbox).
                        maxWidth: 160,
                        maxHeight: 160,
                        borderRadius: 'var(--inic-radius-sm)',
                        border: '1px solid var(--inic-rule)',
                      }}
                    />
                  ) : (
                    <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>
                      No video uploaded — Video mode falls back to the icon launcher.
                    </span>
                  )}
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadVideo(f);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className={styles.buttonGhost}
                    onClick={() => videoInputRef.current?.click()}
                    disabled={saving}
                  >
                    {configDraft.launcherVideoUrl ? 'Replace video' : 'Upload video'}
                  </button>
                  {configDraft.launcherVideoUrl ? (
                    <button
                      type="button"
                      className={styles.buttonGhost}
                      onClick={() => void removeVideo()}
                      disabled={saving}
                    >
                      Remove video
                    </button>
                  ) : null}
                </span>
              </div>
            </div>
          )}

          {tab === 'messaging' && (
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Title</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={60}
                  value={configDraft.chatTitle ?? ''}
                  onChange={(e) => setDraft('chatTitle', e.target.value || null)}
                  placeholder="(blank = &ldquo;Chat · AI assistant&rdquo;)"
                />
                <span className={styles.hint}>
                  Small label above the header. Blank shows the default “Chat · AI assistant”.
                </span>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Header text</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={80}
                  value={configDraft.headerText ?? ''}
                  onChange={(e) => setDraft('headerText', e.target.value || null)}
                  placeholder="(blank = default per-view title)"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Footer text</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={120}
                  value={configDraft.footerText ?? ''}
                  onChange={(e) => setDraft('footerText', e.target.value || null)}
                  placeholder="Live support · usually replies in 2 min"
                />
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--inic-space-2)' }}>
                  <input
                    type="checkbox"
                    checked={configDraft.hideSystemBubbles}
                    onChange={(e) => setDraft('hideSystemBubbles', e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--inic-space-1)' }}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Hide system messages in the widget
                    </span>
                    <span className={styles.hint}>
                      Hides the centered status chips (handoff notices, call events, “AI took over”)
                      on the visitor side. Agents still see them in the conversation.
                    </span>
                  </span>
                </label>
              </div>
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Greeting — shown as the first chat bubble (max 280 chars)</span>
                <input
                  className={styles.input}
                  type="text"
                  value={configDraft.greeting ?? ''}
                  onChange={(e) => setDraft('greeting', e.target.value || null)}
                  placeholder="Talk to a human, or our AI assistant — either way, no menus."
                />
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Conversation starters</span>
                <StringListEditor
                  value={configDraft.conversationStarters}
                  onChange={(next) => setDraft('conversationStarters', next)}
                  placeholder="What are your opening hours?"
                  addLabel="Add starter"
                  emptyText="No starters: visitors begin with an empty message box."
                  max={6}
                  maxLength={120}
                />
                <span className={styles.hint}>
                  Up to 6 questions shown as buttons before the visitor's first message. Tapping one
                  sends it, so the assistant answers straight away. Shown in this order.
                </span>
              </div>
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Offline copy (max 280 chars)</span>
                <input
                  className={styles.input}
                  type="text"
                  value={configDraft.offlineCopy ?? ''}
                  onChange={(e) => setDraft('offlineCopy', e.target.value || null)}
                  placeholder="Our team is offline. Leave a note and we will reply by email."
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Chat routing</span>
                <select
                  className={styles.select}
                  value={configDraft.chatRouting}
                  onChange={(e) =>
                    setDraft('chatRouting', e.target.value as WidgetConfig['chatRouting'])
                  }
                >
                  <option value="ai-first">AI answers first (instant)</option>
                  <option value="agent-first">Agents first (AI only when none online)</option>
                </select>
                <span className={styles.hint}>
                  Either way the AI takes over when no agents are online.
                </span>
              </label>
            </div>
          )}

          {tab === 'prechat' && (
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Pre-chat capture style</span>
                <select
                  className={styles.select}
                  value={configDraft.prechatStyle}
                  onChange={(e) =>
                    setDraft('prechatStyle', e.target.value as WidgetConfig['prechatStyle'])
                  }
                >
                  <option value="form">Classic form</option>
                  <option value="chat">Conversational (asks in chat)</option>
                </select>
              </label>
              {configDraft.prechatStyle === 'form' ? (
                <label className={styles.field}>
                  <span className={styles.label}>Form mode</span>
                  <select
                    className={styles.select}
                    value={configDraft.formMode}
                    onChange={(e) => setDraft('formMode', e.target.value as WidgetConfig['formMode'])}
                  >
                    <option value="optional">Optional (visitor can skip)</option>
                    <option value="required">Required (must fill in)</option>
                    <option value="hidden">Hidden (straight to chat)</option>
                  </select>
                </label>
              ) : (
                <>
                  <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.label}>
                      Fields to collect — the assistant asks for each in chat once the conversation
                      is under way. Leave a question blank to use the built-in wording.
                    </span>
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Field</th><th>Collect</th><th>Required</th>
                            <th>Question</th><th>If invalid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(['name', 'email', 'phone'] as const).map((f) => (
                            <tr key={f}>
                              <td style={{ textTransform: 'capitalize' }}>{f}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={configDraft.prechatFields[f].collect}
                                  onChange={(e) => setPrechatField(f, { collect: e.target.checked })}
                                  aria-label={`Collect ${f}`}
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={configDraft.prechatFields[f].required}
                                  disabled={!configDraft.prechatFields[f].collect}
                                  onChange={(e) => setPrechatField(f, { required: e.target.checked })}
                                  aria-label={`Require ${f}`}
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.input}
                                  type="text"
                                  disabled={!configDraft.prechatFields[f].collect}
                                  value={configDraft.prechatPrompts?.[f] ?? ''}
                                  placeholder={PRECHAT_DEFAULTS[f].question}
                                  aria-label={`${f} question`}
                                  onChange={(e) => setPrechatPrompt(f, e.target.value)}
                                />
                              </td>
                              <td>
                                {f === 'name' ? (
                                  <span style={{ opacity: 0.4 }}>—</span>
                                ) : (
                                  <input
                                    className={styles.input}
                                    type="text"
                                    disabled={!configDraft.prechatFields[f].collect}
                                    value={
                                      configDraft.prechatPrompts?.[
                                        f === 'email' ? 'retryEmail' : 'retryPhone'
                                      ] ?? ''
                                    }
                                    placeholder={PRECHAT_DEFAULTS[f].retry}
                                    aria-label={`${f} retry message`}
                                    onChange={(e) =>
                                      setPrechatPrompt(
                                        f === 'email' ? 'retryEmail' : 'retryPhone',
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <label className={styles.field}>
                    <span className={styles.label}>
                      Ask after — replies the visitor gets before the assistant asks for their
                      details, instead of an AI reply on that turn (0 = ask on their first message)
                    </span>
                    <input
                      className={styles.input}
                      type="number"
                      min={0}
                      max={20}
                      value={configDraft.captureAfter}
                      onChange={(e) =>
                        setDraft(
                          'captureAfter',
                          Math.max(0, Math.min(20, Math.trunc(Number(e.target.value) || 0))),
                        )
                      }
                    />
                  </label>
                  <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.label}>
                      Intro line — optional lead-in statement shown once before the questions (keep
                      it a statement, not a question — the field below is the question)
                    </span>
                    <input
                      className={styles.input}
                      type="text"
                      value={configDraft.prechatPrompts?.intro ?? ''}
                      placeholder="Let me grab a couple of details so we can follow up."
                      onChange={(e) => setPrechatPrompt('intro', e.target.value)}
                    />
                  </label>
                </>
              )}
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={configDraft.newsletterEnabled}
                    onChange={(e) => setDraft('newsletterEnabled', e.target.checked)}
                  />
                  <span className={styles.checkRowText}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Offer newsletter sign-up
                    </span>
                    <span className={styles.hint}>
                      Classic form: an unticked checkbox under the email field. Conversational: once the
                      visitor gives their email, the chat asks once whether they’d like news and offers. Sign-ups
                      appear under <strong>Newsletter</strong>, and go to Mailchimp when it’s connected there.
                    </span>
                  </span>
                </label>
              </div>
              {configDraft.newsletterEnabled && (
                <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>Checkbox text (classic form)</span>
                  <input
                    className={styles.input}
                    maxLength={160}
                    value={configDraft.newsletterLabel ?? ''}
                    placeholder="Send me news and offers by email"
                    onChange={(e) => setDraft('newsletterLabel', e.target.value || null)}
                  />
                </label>
              )}
            </div>
          )}

          {tab === 'ai' && (
            <div className={styles.form}>
              <p className={styles.hint} style={{ gridColumn: '1 / -1', margin: 0 }}>
                Tell the assistant about your business. It uses this in every answer, alongside your{' '}
                <strong>Data sources</strong>. The model and API key are set once for the whole install in{' '}
                <strong>Admin → System → AI</strong>. Try changes in the <strong>Playground</strong> before
                saving.
              </p>
              <CompanyProfileFields
                value={configDraft.companyProfile ?? {}}
                onChange={(next) => setDraft('companyProfile', next)}
              />
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Extra instructions (optional)</span>
                <textarea
                  className={styles.input}
                  rows={6}
                  value={configDraft.systemPrompt ?? ''}
                  onChange={(e) => setDraft('systemPrompt', e.target.value || null)}
                  placeholder="Keep replies under 4 sentences. If the visitor asks about pricing for custom work, offer to connect them with a human agent."
                  style={{ resize: 'vertical', fontFamily: 'var(--inic-font-mono)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.45 }}
                />
                <span className={styles.hint}>
                  Rules for how the assistant behaves. Blank uses the built-in assistant instructions; the
                  company profile above is added either way.
                </span>
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={configDraft.readMoreLinks}
                    onChange={(e) => setDraft('readMoreLinks', e.target.checked)}
                  />
                  <span className={styles.checkRowText}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Add “Read more” links to answers
                    </span>
                    <span className={styles.hint}>
                      When an answer comes from a page of your website (Data sources → Websites), link that
                      page under the answer so the visitor can open it.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {tab === 'handoff' && (
            <div className={styles.form}>
              <p className={styles.hint} style={{ gridColumn: '1 / -1', margin: 0 }}>
                A visitor talking to the AI assistant can ask for a person at any time: with the button
                below, or by simply writing it (“can I talk to a human?”). If someone on the team is
                online, the conversation is handed over to them. If nobody is, the visitor is told so, the
                assistant keeps helping, and the team is notified: in the dashboard, by push, and by email
                to the site admins.
              </p>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={configDraft.humanHandoffButton}
                    onChange={(e) => setDraft('humanHandoffButton', e.target.checked)}
                  />
                  <span className={styles.checkRowText}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Show a “Talk to a person” button in the chat
                    </span>
                    <span className={styles.hint}>
                      Shown while the AI assistant is answering. Asking in words works either way.
                    </span>
                  </span>
                </label>
              </div>
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Message when nobody is online</span>
                <textarea
                  className={styles.input}
                  rows={3}
                  maxLength={500}
                  value={configDraft.handoffOfflineMessage ?? ''}
                  onChange={(e) => setDraft('handoffOfflineMessage', e.target.value || null)}
                  placeholder="Our team isn't online right now, but I've let them know you'd like to talk to a person. They'll reply here, or by email if you leave your address. Meanwhile, I'm happy to keep helping."
                  style={{ resize: 'vertical' }}
                />
              </label>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Also email</span>
                <StringListEditor
                  value={configDraft.handoffNotifyEmails}
                  onChange={(next) => setDraft('handoffNotifyEmails', next)}
                  placeholder="support@example.com"
                  addLabel="Add address"
                  emptyText="Only the site admins are emailed."
                  max={10}
                  maxLength={320}
                  mono
                  validate={(v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Enter an email address.')}
                />
                <span className={styles.hint}>
                  Site admins are always emailed (once per conversation). Mail goes out from this site’s
                  Email (SMTP) settings when set, else the install’s sender.
                </span>
              </div>
              <p className={styles.hint} style={{ gridColumn: '1 / -1', margin: 0 }}>
                Sites with a trained intent model can also hand over when a message <em>means</em> “I want a
                person” — see <strong>Intents</strong> ({configDraft.intentHandoffEnabled ? 'on' : 'off'} for this
                site).
              </p>
            </div>
          )}

          {tab === 'followup' && (
            <div className={styles.form}>
              <p className={styles.hint} style={{ gridColumn: '1 / -1', margin: 0 }}>
                When the visitor goes quiet after getting a reply, send them a short follow-up so the
                conversation doesn’t just stop before their question is resolved. The AI assistant follows
                up in its own name; in a conversation an agent is handling, the follow-up goes out in the name
                of the agent who replied last.
              </p>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={configDraft.followupEnabled}
                    onChange={(e) => setDraft('followupEnabled', e.target.checked)}
                  />
                  <span className={styles.checkRowText}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Follow up when a visitor stops replying
                    </span>
                  </span>
                </label>
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    disabled={!configDraft.followupEnabled}
                    checked={configDraft.followupAgentChats}
                    onChange={(e) => setDraft('followupAgentChats', e.target.checked)}
                  />
                  <span className={styles.checkRowText}>
                    <span className={styles.label} style={{ textTransform: 'none' }}>
                      Also in conversations an agent is handling
                    </span>
                    <span className={styles.hint}>Off: only conversations the AI assistant is handling.</span>
                  </span>
                </label>
              </div>
              <label className={styles.field}>
                <span className={styles.label}>After (minutes of silence)</span>
                <input
                  className={styles.input}
                  type="number"
                  min={1}
                  max={1440}
                  disabled={!configDraft.followupEnabled}
                  value={configDraft.followupAfterMinutes}
                  onChange={(e) =>
                    setDraft('followupAfterMinutes', Math.max(1, Math.min(1440, Math.trunc(Number(e.target.value) || 1))))
                  }
                />
                {configDraft.followupEnabled && configDraft.followupAfterMinutes >= configDraft.autoEndMinutes && (
                  <span className={styles.hintError}>
                    Conversations end after {configDraft.autoEndMinutes} minutes of inactivity (Webhooks → Session
                    auto-end), so this follow-up would never be sent. Pick a shorter delay or a longer auto-end.
                  </span>
                )}
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Follow-ups in a row (at most)</span>
                <select
                  className={styles.select}
                  disabled={!configDraft.followupEnabled}
                  value={configDraft.followupMax}
                  onChange={(e) => setDraft('followupMax', Number(e.target.value))}
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
                <span className={styles.hint}>Counted again from zero whenever the visitor replies.</span>
              </label>
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Message</span>
                <textarea
                  className={styles.input}
                  rows={2}
                  maxLength={500}
                  disabled={!configDraft.followupEnabled}
                  value={configDraft.followupMessage ?? ''}
                  onChange={(e) => setDraft('followupMessage', e.target.value || null)}
                  placeholder="Are you still there? If there’s anything else I can help with, just reply here."
                  style={{ resize: 'vertical' }}
                />
              </label>
            </div>
          )}

          {tab === 'access' && (
            <div className={styles.form}>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Allowed embed origins — websites permitted to load this widget (CORS)
                </span>
                <OriginsEditor
                  value={configDraft.allowedOrigins}
                  onChange={(next) => setDraft('allowedOrigins', next)}
                />
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Always-allowed visitor IPs — addresses or CIDR ranges that always see the
                  widget (overriding the IP and country blocks below) and whose chats are
                  tagged “Internal” in the live monitor and chat history
                </span>
                <IpListEditor
                  value={configDraft.allowedIps ?? []}
                  onChange={(next) => setDraft('allowedIps', next)}
                  addLabel="Allow IP"
                  emptyText="No always-allowed IPs — only the blocks below apply."
                />
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Blocked visitor IPs — addresses or CIDR ranges that won't see the widget
                </span>
                <IpListEditor
                  value={configDraft.blockedIps}
                  onChange={(next) => setDraft('blockedIps', next)}
                  addLabel="Block IP"
                  emptyText="No blocked IPs — every visitor sees the widget."
                />
              </div>
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Blocked countries — visitors geolocating to these won't see the widget
                </span>
                <BlockedCountriesEditor
                  value={configDraft.blockedCountries ?? []}
                  onChange={(next) => setDraft('blockedCountries', next)}
                />
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.button}
              onClick={saveConfig}
              disabled={saving || JSON.stringify(configDraft) === JSON.stringify(config)}
            >
              {saving ? 'Saving…' : 'Save changes →'}
            </button>
            <button
              type="button"
              className={styles.buttonGhost}
              onClick={() => setConfigDraft(config)}
              disabled={saving}
            >
              Reset
            </button>
          </div>
        </section>
      )}

      {tab === 'agents' && (
      <section className={styles.section}>
        <div className={styles.agentsHead}>
          <p className={styles.kicker}>Agents on this site</p>
          <span className={styles.agentsCount}>
            {siteAgents.length} {siteAgents.length === 1 ? 'agent' : 'agents'}
          </span>
        </div>
        <div className={styles.addAgentRow}>
          <label className={styles.addAgentField}>
            <span className={styles.label}>Add agent</span>
            <select className={styles.select} value={picker} onChange={(e) => setPicker(e.target.value)}>
              <option value="">
                {availableAgents.length ? '— pick an agent —' : 'Every agent is already on this site'}
              </option>
              {availableAgents.map((a) => (
                <option key={a.id} value={a.id}>{a.displayName} · {a.email}</option>
              ))}
            </select>
          </label>
          <button type="button" className={styles.button} disabled={!picker} onClick={addAgent}>
            Add to site →
          </button>
        </div>

        {notice && (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Agent</th><th>Email</th><th>Phone</th><th>Role</th><th>Site admin</th>
                <th className={styles.actionsCol}><span className={styles.srOnly}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {siteAgents.map((a) => {
                const isDisabled = !!a.disabledAt;
                const roleClass =
                  a.role === 'super-admin' ? styles.roleSuper : a.role === 'account-admin' ? styles.roleAccount : styles.roleAgent;
                return (
                <tr key={a.agentId} className={isDisabled ? styles.rowDisabled : undefined}>
                  <td>
                    <span className={styles.nameCell}>
                      <Avatar name={a.displayName} avatarUrl={a.avatarUrl} size={32} />
                      <span className={styles.nameText}>{a.displayName}</span>
                      {isDisabled && (
                        <span className={`${styles.badge} ${styles.badgeMuted}`}>disabled</span>
                      )}
                    </span>
                  </td>
                  <td className={styles.mono}>{a.email}</td>
                  <td className={styles.mono}>{a.phone ?? '—'}</td>
                  <td>
                    <span className={`${styles.roleBadge} ${roleClass}`}>{a.role.replace('-', ' ')}</span>
                  </td>
                  <td>
                    {/* A disabled agent can't be granted/changed site-admin —
                        only removed from the site. */}
                    <button type="button"
                      className={`${styles.siteAdminToggle} ${a.isSiteAdmin ? styles.siteAdminOn : ''}`}
                      aria-pressed={a.isSiteAdmin}
                      aria-label={`Site admin for ${a.displayName}`}
                      title={a.isSiteAdmin ? 'Site admin — click to remove' : 'Not a site admin — click to grant'}
                      disabled={isDisabled}
                      onClick={() => toggleSiteAdmin(a.agentId, a.isSiteAdmin)}>
                      {a.isSiteAdmin
                        ? <ShieldCheck size={18} strokeWidth={2.25} aria-hidden="true" />
                        : <Shield size={18} strokeWidth={1.75} aria-hidden="true" />}
                    </button>
                  </td>
                  <td className={styles.actionsCol}>
                    {/* Removing a disabled agent stays allowed. */}
                    <button type="button" className={styles.buttonDanger}
                      aria-label={`Remove ${a.displayName} from this site`}
                      onClick={() => removeAgent(a.agentId)}>Remove</button>
                  </td>
                </tr>
                );
              })}
              {siteAgents.length === 0 && (
                <tr><td colSpan={6} className={styles.emptyRow}>No agents assigned yet. Pick one above to add them.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {tab === 'import' && <TidioImportSection siteId={siteId} />}
      {tab === 'channels' && <ChannelsSection siteId={siteId} />}
      {tab === 'shopify' && <ShopifySection key={siteId} siteId={siteId} />}
      {tab === 'suggestions' && <SuggestionsSection siteId={siteId} />}
      {tab === 'email' && <EmailSection siteId={siteId} />}
      {tab === 'newsletter' && <NewsletterSection siteId={siteId} optInOffered={config?.newsletterEnabled ?? false} />}
      {tab === 'playground' && (
        <PlaygroundSection
          siteId={siteId}
          starters={config?.conversationStarters ?? []}
          unsaved={
            configDraft &&
            config &&
            (configDraft.systemPrompt !== config.systemPrompt ||
              JSON.stringify(configDraft.companyProfile) !== JSON.stringify(config.companyProfile))
              ? { systemPrompt: configDraft.systemPrompt, companyProfile: configDraft.companyProfile }
              : null
          }
        />
      )}
      {tab === 'csat' && <CsatSection siteId={siteId} />}
      {tab === 'knowledge' && <KnowledgeBaseSection siteId={siteId} />}
      {tab === 'ai-eval' && <RagEvalSection siteId={siteId} />}
      {tab === 'intents' && <IntentsSection siteId={siteId} />}

      {tab === 'commands' && <CommandsSection siteId={siteId} />}
      {tab === 'routing' && <RoutingSection siteId={siteId} />}
      {tab === 'flows' && flowsEnabled && <FlowsSection siteId={siteId} />}

      {tab === 'webhooks' && <WebhooksSection siteId={siteId} />}

      {tab === 'webhook-logs' && <WebhookLogsSection siteId={siteId} />}
        </div>
      </div>
    </>
  );
}

/** The company profile's fields; an emptied field is stored as null. */
function CompanyProfileFields({
  value,
  onChange,
}: {
  value: CompanyProfile;
  onChange: (next: CompanyProfile | null) => void;
}) {
  const set = (key: keyof CompanyProfile, v: string) => {
    const next = { ...value, [key]: v.trim() ? v : null };
    onChange(Object.values(next).some((x) => x) ? next : null);
  };
  const area = (key: keyof CompanyProfile, label: string, placeholder: string, rows = 3) => (
    <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
      <span className={styles.label}>{label}</span>
      <textarea
        className={styles.input}
        rows={rows}
        maxLength={4000}
        value={(value[key] as string | null | undefined) ?? ''}
        placeholder={placeholder}
        onChange={(e) => set(key, e.target.value)}
        style={{ resize: 'vertical' }}
      />
    </label>
  );
  return (
    <>
      <label className={styles.field}>
        <span className={styles.label}>Company name</span>
        <input
          className={styles.input}
          maxLength={120}
          value={value.name ?? ''}
          placeholder="Acme Store"
          onChange={(e) => set('name', e.target.value)}
        />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Website</span>
        <input
          className={styles.input}
          maxLength={300}
          value={value.website ?? ''}
          placeholder="https://acme.example"
          onChange={(e) => set('website', e.target.value)}
        />
      </label>
      {area('about', 'About the company', 'Who you are, what you do and what makes you different.', 4)}
      {area('offering', 'Products and services', 'What you sell or offer, with prices or ranges where you are happy to share them.')}
      {area('audience', 'Customers', 'Who your customers are and what they usually need help with.', 2)}
      {area('policies', 'Policies', 'Shipping, returns, refunds, warranty, payment options, opening hours.')}
      {area('contact', 'How to reach the team', 'Phone, email, address and when someone is available.', 2)}
      <label className={styles.field}>
        <span className={styles.label}>Tone of voice</span>
        <select
          className={styles.select}
          value={value.tone ?? ''}
          onChange={(e) => {
            const tone = (e.target.value || null) as CompanyTone | null;
            const next = { ...value, tone };
            onChange(Object.values(next).some((x) => x) ? next : null);
          }}
        >
          <option value="">Default</option>
          {(Object.keys(COMPANY_TONES) as CompanyTone[]).map((t) => (
            <option key={t} value={t}>
              {t[0]!.toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

/** A px offset; blank = the widget's built-in spacing. */
function OffsetField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <input
        className={styles.input}
        type="number"
        min={0}
        max={400}
        value={value ?? ''}
        placeholder="24 (default)"
        onChange={(e) => {
          const raw = e.target.value.trim();
          onChange(raw === '' ? null : Math.max(0, Math.min(400, Math.trunc(Number(raw) || 0))));
        }}
      />
    </label>
  );
}

// ─── Integration tab ───────────────────────────────────────────────────────
// Self-serve integration guide, rendered with the site's REAL embed token so
// it's copy-paste ready. This is the in-product mirror of
// docs/widget-integration.md — KEEP THE TWO IN SYNC when anything that affects
// host-page integration changes (the embed snippet, the window.Openline API,
// events, or the availability/fallback contract).

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={styles.buttonGhost}
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        window.setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? 'Copied ✓' : label}
    </button>
  );
}

function IntegrationSection({ site }: { site: Site }) {
  const embedSnippet = `<script
  src="${serverOrigin()}/openline.js"
  data-site-token="${site.embedToken}"
  async defer
></script>`;

  const fallbackSnippet = `<!-- After the Openline embed script -->
<script>
  function onOpenline(available) {
    // Show your own fallback (contact form, "leave a message", …) whenever
    // no agent is online; hide it when one is.
    document.getElementById('chat-fallback').hidden = available;
  }

  // React to live changes (agents coming online / going offline):
  window.addEventListener('openline:availability', function (e) {
    onOpenline(e.detail.available); // e.detail.reason === 'no-agents-online'
  });

  // Or subscribe via the API — fires immediately with the current state,
  // so you never miss the first transition:
  window.Openline && window.Openline.onAvailability(onOpenline);
</script>`;

  // The full framework-agnostic brief (docs/integrate-openline.md) with THIS
  // site's real host + token substituted for the placeholders, ready to paste
  // into any coding agent. Scheme-aware so http/https + ws/wss stay correct.
  const origin = serverOrigin();
  let host = origin;
  let httpBase = origin;
  let wsBase = origin;
  try {
    const u = new URL(origin, window.location.origin);
    host = u.host;
    httpBase = `${u.protocol}//${u.host}`;
    wsBase = `${u.protocol === 'https:' ? 'wss' : 'ws'}://${u.host}`;
  } catch {
    /* keep raw origin as fallback */
  }
  const agentBrief = integrateGuide
    .replaceAll('https://OPENLINE_HOST', httpBase)
    .replaceAll('wss://OPENLINE_HOST', wsBase)
    .replaceAll('OPENLINE_HOST', host)
    .replaceAll('SITE_TOKEN', site.embedToken);

  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const agentModalRef = useRef<HTMLDivElement>(null);
  useDialogFocus(agentModalRef, () => setAgentModalOpen(false), { open: agentModalOpen });
  const [briefCopied, setBriefCopied] = useState(false);
  function copyBrief() {
    navigator.clipboard?.writeText(agentBrief);
    setBriefCopied(true);
    window.setTimeout(() => setBriefCopied(false), 1500);
  }

  return (
    <>
      <section className={styles.section}>
        <p className={styles.kicker}>Integration</p>
        <h2 className={styles.title}>Add Openline to your website</h2>
      </section>

      <section className={styles.section}>
        <div className={styles.integrationSub}>
          <h3>Hand it to a coding agent</h3>
          <p>
            Prefer to let AI wire it up? Open the complete, tech-stack-agnostic setup brief —
            this site's host and token are already filled in — and paste it into{' '}
            <strong>Claude, Gemini, Codex</strong>, or any other coding agent. It covers React,
            Vue, Angular, Next.js, WordPress, plain HTML, and more.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.buttonGhost}
              onClick={() => setAgentModalOpen(true)}
            >
              View instructions for a coding agent →
            </button>
          </div>
        </div>

        <div className={styles.integrationSub}>
          <h3>1. Embed snippet</h3>
          <p>
            Paste this once, ideally just before <code>&lt;/body&gt;</code>, on any page whose
            origin is listed under <strong>Access &amp; security → Allowed embed origins</strong>. The
            token below is this site's — already filled in.
          </p>
          <pre className={styles.snippet}>{embedSnippet}</pre>
          <div className={styles.actions}>
            <CopyButton text={embedSnippet} label="Copy embed code" />
            <CopyButton text={site.embedToken} label="Copy token" />
          </div>
        </div>

        <div className={styles.integrationSub}>
          <h3>2. Page tracking</h3>
          <p>
            The widget builds a timeline of the pages a visitor views. It hooks the browser History
            API automatically, so <strong>React Router, Angular, Vue, Next.js, SvelteKit</strong>{' '}
            and most routers are captured with zero extra code.
          </p>
          <p>
            Only report manually for navigations that don't change the URL (a wizard step, a modal
            treated as a page) or routers that bypass the History API:
          </p>
          <pre className={styles.snippet}>{`window.Openline?.page?.();                 // current location.href
window.Openline?.page?.('/checkout/step-2'); // a specific URL`}</pre>
          <p>Manual calls are de-duplicated against auto-tracking, so duplicates are safe.</p>
        </div>

        <div className={styles.integrationSub}>
          <h3>3. Render your own fallback when no agent is online</h3>
          <p>
            When <strong>Launcher &amp; visibility → “Only show the widget when an agent is online”</strong>{' '}
            is enabled, the launcher is hidden while no agent is online. The embed stays connected
            and the launcher <strong>reappears live</strong> when one comes online — and your page
            is notified each time so you can render your own fallback.
          </p>
          <pre className={styles.snippet}>{fallbackSnippet}</pre>
          <div className={styles.actions}>
            <CopyButton text={fallbackSnippet} label="Copy fallback code" />
          </div>
          <p style={{ marginTop: 'var(--inic-space-3)' }}>Contract:</p>
          <table className={styles.integrationTable}>
            <thead>
              <tr>
                <th>Surface</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>window.Openline.available</code></td>
                <td><code>boolean</code> — <code>true</code> when the launcher is shown</td>
              </tr>
              <tr>
                <td><code>openline:availability</code> event</td>
                <td><code>{`e.detail = { available, reason: 'no-agents-online' | null }`}</code></td>
              </tr>
              <tr>
                <td><code>window.Openline.onAvailability(cb)</code></td>
                <td>
                  <code>cb(available, {'{ reason }'})</code> — replays current state on subscribe;
                  returns an unsubscribe fn
                </td>
              </tr>
            </tbody>
          </table>
          <ul>
            <li>
              <code>available</code> starts <code>true</code> and only flips after the widget reads
              its config. A late <code>addEventListener</code> can miss the first flip — use{' '}
              <code>onAvailability</code> (it replays) or read <code>window.Openline.available</code>{' '}
              once on setup.
            </li>
            <li>An open conversation is never interrupted — the toggle gates the launcher only.</li>
            <li>
              Prefer a server-side check? <code>GET /api/widget/config?token=…</code> returns{' '}
              <code>requireAgentOnline</code> and a point-in-time <code>agentsOnline</code>.
            </li>
          </ul>
        </div>
      </section>

      {agentModalOpen && (
        <div
          ref={agentModalRef}
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Instructions for a coding agent"
          onClick={() => setAgentModalOpen(false)}
        >
          <div
            className={styles.modal}
            style={{ maxWidth: 860, maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--inic-space-3)',
              }}
            >
              <h2 className={styles.modalTitle}>Instructions for a coding agent</h2>
              <div style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
                <button
                  type="button"
                  className={styles.buttonGhost}
                  onClick={copyBrief}
                  aria-label="Copy instructions"
                  title="Copy instructions"
                >
                  {briefCopied ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  )}
                  <span style={{ marginLeft: 'var(--inic-space-1)' }}>{briefCopied ? 'Copied' : 'Copy'}</span>
                </button>
                <button
                  type="button"
                  className={styles.buttonGhost}
                  onClick={() => setAgentModalOpen(false)}
                  aria-label="Close"
                  title="Close"
                >
                  ✕
                </button>
              </div>
            </div>
            <p className={styles.modalSubtitle}>
              Works with Claude, Gemini, Codex, or any coding agent — host + token already filled in.
            </p>
            <pre
              className={styles.snippet}
              style={{ margin: 0, overflow: 'auto', whiteSpace: 'pre-wrap', flex: '1 1 auto', minHeight: 0 }}
            >
              {agentBrief}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

// ─── "Only show when an agent is online" help popover ──────────────────────
// Explains how a host site reacts when the widget hides itself for lack of an
// online agent: a `openline:availability` window event + window.Openline API.
const AGENT_ONLINE_SNIPPET = `<!-- On your site, after the Openline embed script -->
<script>
  function onOpenline(available) {
    // Show your own fallback (contact form, "leave a message", etc.)
    // whenever no agent is online; hide it when one is.
    document.getElementById('chat-fallback').hidden = available;
  }

  // 1) React to live changes (agents coming online / going offline):
  window.addEventListener('openline:availability', function (e) {
    onOpenline(e.detail.available); // e.detail.reason === 'no-agents-online'
  });

  // 2) Or read the current state any time:
  //    window.Openline && window.Openline.onAvailability(onOpenline);
</script>`;

function AgentOnlineHelp() {
  const [open, setOpen] = useState(false);
  return (
    <span className={styles.helpWrap}>
      <button
        type="button"
        className={styles.helpBtn}
        aria-label="How to handle this on your website"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ?
      </button>
      {open && (
        <span className={styles.helpPop} role="dialog">
          <p>
            When no agent is online the launcher is hidden and your page is notified, so you can
            render your own fallback. Listen for the <code>openline:availability</code> event or use{' '}
            <code>window.Openline.onAvailability()</code>:
          </p>
          <code className={styles.helpCode}>{AGENT_ONLINE_SNIPPET}</code>
        </span>
      )}
    </span>
  );
}

// ─── Allowed embed origins editor ─────────────────────────────────────────
// Manages the per-site CORS allowlist. Each entry is normalized to a
// canonical origin (scheme + host + port); the server re-validates + dedupes
// on save. A bare host is assumed https://. Saved with the widget config.
function OriginsEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function add() {
    const raw = input.trim();
    if (!raw) return;
    let origin: string;
    try {
      origin = new URL(raw).origin;
    } catch {
      try {
        origin = new URL(`https://${raw}`).origin;
      } catch {
        setErr('Enter a valid URL, e.g. https://example.com');
        return;
      }
    }
    if (value.includes(origin)) {
      setErr('Already added.');
      setInput('');
      return;
    }
    onChange([...value, origin]);
    setInput('');
    setErr(null);
  }

  return (
    <div>
      <div className={styles.originList}>
        {value.length === 0 && (
          <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>
            No external origins yet — the widget only runs same-origin until you add one.
          </span>
        )}
        {value.map((o) => (
          <span key={o} className={styles.originChip}>
            <span className={styles.mono}>{o}</span>
            <button
              type="button"
              aria-label={`Remove ${o}`}
              onClick={() => onChange(value.filter((x) => x !== o))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
        <input
          className={styles.input}
          style={{ flex: 1, minWidth: 0 }}
          value={input}
          placeholder="https://customer-site.com"
          onChange={(e) => {
            setInput(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className={styles.buttonGhost} onClick={add}>
          Add origin
        </button>
      </span>
      {err && (
        <span className={styles.error} style={{ fontSize: 'var(--inic-size-xs)' }}>
          {err}
        </span>
      )}
    </div>
  );
}

// Lightweight client-side check for an exact IP or CIDR range. The server
// (ip-block.ts) is the authority and re-validates on save; this just catches
// obvious typos before the round-trip.
function isValidIpRule(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  if (!v) return false;
  const slash = v.indexOf('/');
  const addr = slash === -1 ? v : v.slice(0, slash);
  const isV4 = /^(\d{1,3})(\.\d{1,3}){3}$/.test(addr) &&
    addr.split('.').every((o) => Number(o) <= 255);
  // Permissive IPv6: hex groups + at most one "::", optional embedded IPv4.
  const isV6 = /^[0-9a-f:]+(\.\d{1,3}){0,3}$/.test(addr) && addr.includes(':') &&
    (addr.match(/::/g)?.length ?? 0) <= 1;
  if (!isV4 && !isV6) return false;
  if (slash !== -1) {
    const p = Number(v.slice(slash + 1));
    const max = isV4 ? 32 : 128;
    if (!Number.isInteger(p) || p < 0 || p > max) return false;
  }
  return true;
}

// Reusable IP/CIDR list editor — used for both the blocklist and the allowlist
// (which differ only in copy). Each entry is an exact IPv4/IPv6 address or a
// CIDR range; the server re-validates + dedupes on save.
function IpListEditor({
  value,
  onChange,
  addLabel,
  emptyText,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  emptyText: string;
}) {
  const [input, setInput] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function add() {
    const raw = input.trim().toLowerCase();
    if (!raw) return;
    if (!isValidIpRule(raw)) {
      setErr('Enter an IP (203.0.113.5) or CIDR range (203.0.113.0/24).');
      return;
    }
    if (value.includes(raw)) {
      setErr('Already added.');
      setInput('');
      return;
    }
    onChange([...value, raw]);
    setInput('');
    setErr(null);
  }

  return (
    <div>
      <div className={styles.originList}>
        {value.length === 0 && (
          <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>
            {emptyText}
          </span>
        )}
        {value.map((ip) => (
          <span key={ip} className={styles.originChip}>
            <span className={styles.mono}>{ip}</span>
            <button
              type="button"
              aria-label={`Remove ${ip}`}
              onClick={() => onChange(value.filter((x) => x !== ip))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
        <input
          className={styles.input}
          style={{ flex: 1, minWidth: 0 }}
          value={input}
          placeholder="203.0.113.5 or 203.0.113.0/24"
          onChange={(e) => {
            setInput(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className={styles.buttonGhost} onClick={add}>
          {addLabel}
        </button>
      </span>
      {err && (
        <span className={styles.error} style={{ fontSize: 'var(--inic-size-xs)' }}>
          {err}
        </span>
      )}
    </div>
  );
}

// Regional-indicator flag emoji from an ISO-2 code (e.g. 'IN' → 🇮🇳). Pure
// codepoint math — no asset. Falls back to nothing for odd input.
function flagEmoji(code: string): string {
  if (code.length !== 2) return '';
  const A = 0x1f1e6;
  const a = 'A'.charCodeAt(0);
  return String.fromCodePoint(
    A + (code.charCodeAt(0) - a),
    A + (code.charCodeAt(1) - a),
  );
}

function countryName(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

// Searchable country picker for the per-site country blocklist. Stores ISO-2
// codes; shows names + flags. Mirrors BlockedIpsEditor's chip + add-row layout.
function BlockedCountriesEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const matches =
    q.length === 0
      ? []
      : COUNTRIES.filter(
          (c) =>
            !value.includes(c.code) &&
            (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)),
        ).slice(0, 8);

  function add(code: string) {
    if (!value.includes(code)) onChange([...value, code]);
    setQuery('');
  }

  return (
    <div>
      <div className={styles.originList}>
        {value.length === 0 && (
          <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>
            No blocked countries — visitors from anywhere see the widget.
          </span>
        )}
        {value.map((code) => (
          <span key={code} className={styles.originChip}>
            <span>
              {flagEmoji(code)} {countryName(code)}{' '}
              <span className={styles.mono}>({code})</span>
            </span>
            <button
              type="button"
              aria-label={`Remove ${countryName(code)}`}
              onClick={() => onChange(value.filter((x) => x !== code))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          className={styles.input}
          style={{ width: '100%', minWidth: 0 }}
          value={query}
          placeholder="Search a country to block — e.g. China, RU…"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matches[0]) {
              e.preventDefault();
              add(matches[0].code);
            }
          }}
        />
        {matches.length > 0 && (
          <div
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              marginTop: 'var(--inic-space-1)',
              background: 'var(--inic-paper)',
              border: '1px solid var(--inic-rule)',
              borderRadius: 'var(--inic-radius-sm)',
              boxShadow: 'var(--inic-shadow-soft, 0 4px 16px rgba(0,0,0,0.12))',
              overflow: 'hidden',
            }}
          >
            {matches.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => add(c.code)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 'var(--inic-space-2) var(--inic-space-3)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--inic-size-sm)',
                  color: 'var(--inic-ink)',
                }}
              >
                {flagEmoji(c.code)} {c.name}{' '}
                <span className={styles.mono} style={{ color: 'var(--inic-ink-muted)' }}>
                  {c.code}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <span
        style={{
          display: 'block',
          marginTop: 'var(--inic-space-1)',
          color: 'var(--inic-ink-muted)',
          fontSize: 'var(--inic-size-xs)',
        }}
      >
        Country is resolved from the visitor's IP (≈country-level accuracy; VPNs
        can bypass it). Geolocation data by DB-IP (CC-BY-4.0).
      </span>
    </div>
  );
}

// ─── /commands section ───────────────────────────────────────────────────
// Site-admin / super-admin manages the list of slash-command snippets
// that agents can insert into the composer with `/shortcut`. Each row is
// editable in-place; saves happen on blur or explicit Save click.
function CommandsSection({ siteId }: { siteId: string }) {
  const [commands, setCommands] = useState<SiteCommand[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ shortcut: '', label: '', body: '' });

  async function load() {
    try {
      const { commands } = await apiFetch<{ commands: SiteCommand[] }>(
        `/api/admin/sites/${siteId}/commands`,
      );
      // The site's shared commands only; the caller's personal ones are
      // managed on the agent Commands page.
      setCommands(commands.filter((c) => !c.ownerAgentId));
      setError(null);
    } catch {
      setError('Could not load commands.');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const shortcut = draft.shortcut.trim();
    const label = draft.label.trim();
    const body = draft.body;
    if (!shortcut || !label || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/admin/sites/${siteId}/commands`, {
        method: 'POST',
        body: JSON.stringify({ shortcut, label, body, sortOrder: commands.length }),
      });
      setDraft({ shortcut: '', label: '', body: '' });
      invalidateCommandsCache(siteId);
      await load();
    } catch (err) {
      if (isApiError(err) && (err.body as { error?: string })?.error === 'shortcut-taken') {
        setError(`A command with the shortcut "/${shortcut}" already exists.`);
      } else if (isApiError(err) && err.status === 400) {
        setError('Shortcut must start with a letter and use only letters, digits, _ or -.');
      } else {
        setError('Could not create command.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function update(id: string, patch: Partial<SiteCommand>) {
    setError(null);
    try {
      await apiFetch(`/api/admin/sites/${siteId}/commands/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      invalidateCommandsCache(siteId);
      await load();
    } catch (err) {
      if (isApiError(err) && (err.body as { error?: string })?.error === 'shortcut-taken') {
        setError('That shortcut is already taken on this site.');
      } else {
        setError('Save failed.');
      }
    }
  }

  async function remove(id: string) {
    if (!(await confirmDialog({ title: 'Delete this command?' }))) return;
    setError(null);
    try {
      await apiFetch(`/api/admin/sites/${siteId}/commands/${id}`, {
        method: 'DELETE',
      });
      invalidateCommandsCache(siteId);
      await load();
    } catch {
      setError('Delete failed.');
    }
  }

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Slash commands</p>
      <h2 className={styles.title}>Canned replies for agents.</h2>
      <p style={{ marginTop: 0, color: 'var(--inic-ink-subtle)', fontSize: 'var(--inic-size-sm)' }}>
        Agents type <code>/shortcut</code> in the chat composer to insert these snippets.
        Bodies are plain text — emoji and unicode work as expected.
      </p>

      {error && <p className={styles.error} role="alert">{error}</p>}

      <form className={styles.form} onSubmit={handleCreate}>
        <label className={styles.field}>
          <span className={styles.label}>Shortcut</span>
          <input
            className={styles.input}
            value={draft.shortcut}
            onChange={(e) => setDraft((d) => ({ ...d, shortcut: e.target.value }))}
            placeholder="greet"
            required
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Label</span>
          <input
            className={styles.input}
            value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            placeholder="Friendly greeting"
            required
          />
        </label>
        <label className={`${styles.field}`} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.label}>Body</span>
          <textarea
            className={styles.input}
            style={{ minHeight: 64, fontFamily: 'inherit' }}
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            placeholder="Hi {{visitor}}, how can I help today?"
            required
          />
        </label>
        <div className={styles.formActions}>
          <button type="submit" className={styles.button} disabled={busy}>
            {busy ? 'Adding…' : 'Add command →'}
          </button>
        </div>
      </form>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>Shortcut</th><th>Label</th><th>Body</th><th>Order</th><th></th></tr>
          </thead>
          <tbody>
            {commands.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--inic-ink-subtle)' }}>No commands yet.</td></tr>
            ) : (
              commands.map((c) => (
                <CommandRow
                  key={c.id}
                  command={c}
                  onSave={(patch) => update(c.id, patch)}
                  onDelete={() => remove(c.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CommandRow({
  command,
  onSave,
  onDelete,
}: {
  command: SiteCommand;
  onSave: (patch: Partial<SiteCommand>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [shortcut, setShortcut] = useState(command.shortcut);
  const [label, setLabel] = useState(command.label);
  const [body, setBody] = useState(command.body);
  const [sortOrder, setSortOrder] = useState(command.sortOrder);

  function reset() {
    setShortcut(command.shortcut);
    setLabel(command.label);
    setBody(command.body);
    setSortOrder(command.sortOrder);
  }

  async function save() {
    const patch: Partial<SiteCommand> = {};
    if (shortcut !== command.shortcut) patch.shortcut = shortcut;
    if (label !== command.label) patch.label = label;
    if (body !== command.body) patch.body = body;
    if (sortOrder !== command.sortOrder) patch.sortOrder = sortOrder;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    await onSave(patch);
    setEditing(false);
  }

  if (!editing) {
    return (
      <tr>
        <td className={styles.mono}>/{command.shortcut}</td>
        <td>{command.label}</td>
        <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{command.body}</td>
        <td>{command.sortOrder}</td>
        <td className={styles.actions}>
          <button type="button" className={styles.buttonGhost} onClick={() => setEditing(true)}>Edit</button>
          <button type="button" className={styles.buttonDanger} onClick={() => void onDelete()}>delete</button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td><input className={styles.input} value={shortcut} onChange={(e) => setShortcut(e.target.value)} /></td>
      <td><input className={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} /></td>
      <td>
        <textarea
          className={styles.input}
          style={{ minHeight: 60, fontFamily: 'inherit', width: '100%' }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </td>
      <td>
        <input
          className={styles.input}
          type="number"
          min={0}
          max={10000}
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          style={{ width: 64 }}
        />
      </td>
      <td className={styles.actions}>
        <button type="button" className={styles.button} onClick={() => void save()}>Save</button>
        <button type="button" className={styles.buttonGhost} onClick={() => { reset(); setEditing(false); }}>Cancel</button>
      </td>
    </tr>
  );
}
